import {
  CustomMappingType,
  IFailedCustomMappingRecord,
  ISourceEntityTypes,
  ITranslationMap,
  type ICustomMappingSourceOption,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import { Brand, stafferToNamedDoc } from '@principle-theorem/principle-core';
import { type IStaffer } from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  IBlobFilenamePair,
  WithPrimaryKey,
  XSLXImporterExporter,
  isSameRef,
  multiMap,
  multiSortBy$,
  nameSorter,
  snapshot,
  undeletedQuery,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import Fuse from 'fuse.js';
import { compact, first, sortBy, uniqBy } from 'lodash';
import { from } from 'rxjs';
import { BaseCustomMappingHandler } from '../base-custom-mapping-handler';
import { CustomMapping } from '../custom-mapping';
import { IBaseMigrationStaffer } from '../interfaces';
import { StafferToPractitionerToXSLX } from '../mappings/staffer-to-practitioner-to-xlsx';
import { XSLXToStafferToPractitioner } from '../mappings/xlsx-to-staffer-to-practitioner';
import { BaseSourceEntity } from '../source/base-source-entity';
import { TranslationMapHandler } from '../translation-map';
import { PracticeMigration } from '../practice-migrations';

export const STAFF_CUSTOM_MAPPING_TYPE = 'staffCustomMapping';

export const STAFFER_MAPPING = CustomMapping.init({
  metadata: {
    label: 'Staff',
    description: `Used for mapping Practitioners to the correct Staffer in Principle.`,
    type: STAFF_CUSTOM_MAPPING_TYPE,
  },
  type: CustomMappingType.DocumentReference,
  labelOverrides: {
    sourceIdentifier: 'Id',
    sourceLabel: 'Staff Member',
    destinationIdentifier: 'Principle Staff Member',
  },
});

export abstract class StafferMappingHandler<
  Staffer extends ISourceEntityTypes,
  SourceEntity extends BaseSourceEntity<Staffer>,
> extends BaseCustomMappingHandler<IStaffer> {
  readonly requireDestinationCompleteness = true;
  customMapping = STAFFER_MAPPING;
  abstract translateFn: (record: Staffer['data']) => IBaseMigrationStaffer;
  abstract sourceEntity: SourceEntity;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const translationMap = new TranslationMapHandler(migration);
    const existingRecords = (
      await translationMap.getByType(this.customMapping.metadata.type)
    ).map((record) => ({
      label: record.sourceLabel || '',
      value: record.sourceIdentifier,
    }));

    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    const options = records
      .map((record) => record.data)
      .map((record) => {
        const staffer = this.translateFn(record);
        return {
          label: `${staffer.name} ${staffer.providerNo}`,
          value: this.sourceEntity.getSourceRecordId(record).toString(),
        };
      });

    return sortBy(
      uniqBy(
        [
          ...options,
          ...existingRecords,
          {
            label: `Default Practitioner (When no practitioner is found)`,
            value: '0',
          },
        ],
        'value'
      ),
      'label'
    );
  }

  async getDestinationOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<INamedDocument<IStaffer>[]> {
    return snapshot(
      from(
        Firestore.getDocs(
          undeletedQuery(Brand.stafferCol(migration.configuration.brand))
        )
      ).pipe(
        multiMap((staffer) => ({
          name: staffer.user.name,
          ref: staffer.ref,
        })),
        multiSortBy$(nameSorter())
      )
    );
  }

  async autocompleteMappings(
    migration: WithRef<IPracticeMigration>
  ): Promise<void> {
    const translationMap = new TranslationMapHandler(migration);
    const sourceOptions = await this.getSourceOptions(migration);
    const destinationOptions = await this.getDestinationOptions(migration);
    const records = await this.getRecords(translationMap);

    const mappedItemCodes = records
      .filter((record) => !!record.destinationIdentifier)
      .map((record) => record.sourceIdentifier);
    const unmappedItemCodes = sourceOptions.filter(
      (option) => !mappedItemCodes.includes(option.value)
    );

    const uploadRecords = unmappedItemCodes.map((item) => {
      const fuse = new Fuse(destinationOptions, {
        keys: ['name'],
        includeScore: true,
        threshold: 0.3,
      });
      const matches = fuse.search(item.label);
      const match = first(matches)?.item;

      return match
        ? {
            destinationIdentifier: match.ref,
            destinationValue: { value: match.name },
            sourceIdentifier: item.value,
            sourceLabel: item.label,
          }
        : undefined;
    });

    await this.upsertBulk(compact(uploadRecords), translationMap);
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, appointmentStatuses, translator } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(
      fileName,
      appointmentStatuses,
      translator
    );
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, appointmentStatuses, translator } =
      await this._getExporterData(migration);

    await new XSLXImporterExporter().download(
      fileName,
      appointmentStatuses,
      translator
    );
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XSLXToStafferToPractitioner()
    );

    const translationMap = new TranslationMapHandler(migration);
    const sourceOptions = await this.getSourceOptions(migration);
    const staff = await snapshot(Brand.staff$(migration.configuration.brand));

    const failedRecords: IFailedCustomMappingRecord[] = [];

    const records = items.map((item) => {
      const matchingOption = sourceOptions.find(
        (sourceOption) => sourceOption.value === item.id
      );

      if (!matchingOption || !item.mapTo) {
        return;
      }

      const label = matchingOption.label;
      const value = matchingOption.value;

      const staffer = staff.find(
        (searchStaffer) => searchStaffer.user.name === item.mapTo
      );

      if (!staffer) {
        // eslint-disable-next-line no-console
        console.error(
          `Mapping error: ${this.customMapping.metadata.label} - Couldn't find staffer for item`,
          item
        );
        failedRecords.push({
          sourceIdentifier: value,
          sourceLabel: label,
          reason: `Couldn't find staffer for: ${item.mapTo}`,
        });
        return;
      }

      return {
        destinationIdentifier: staffer.ref,
        destinationValue: { value: item.mapTo },
        sourceIdentifier: value,
        sourceLabel: label,
      };
    });

    const results = await this.upsertBulk(compact(records), translationMap);
    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords: [...failedRecords, ...results.failedRecords],
    });
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    appointmentStatuses: IBaseMigrationStaffer[];
    translator: StafferToPractitionerToXSLX;
  }> {
    const fileName = this.getFileName();
    const appointmentStatuses = await this._getUserOptions(migration);
    const translationMap = new TranslationMapHandler(migration);
    const translator = new StafferToPractitionerToXSLX(
      await snapshot(Brand.staff$(migration.configuration.brand)),
      await this.getRecords(translationMap)
    );
    return { fileName, appointmentStatuses, translator };
  }

  private async _getUserOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBaseMigrationStaffer[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    return sortBy(
      uniqBy(
        records.map((record) => this.translateFn(record.data)),
        (record) => record.id
      ),
      'name'
    );
  }
}

export function getPractitionerOrDefaultMapping(
  practitionerId: string | number | undefined,
  staffMap: WithPrimaryKey<ITranslationMap<IStaffer>>[]
): ITranslationMap<IStaffer> | undefined {
  const defaultProviderMap = staffMap.find(
    (staffer) => staffer.sourceIdentifier === '0'
  );

  const providerId = practitionerId?.toString();
  const providerMap = staffMap.find(
    (staffer) => staffer.sourceIdentifier === providerId
  );

  if (!providerMap && !defaultProviderMap) {
    return;
  }

  return providerMap || defaultProviderMap;
}

export function getPractitionerOrDefault(
  practitionerId: string | number | undefined,
  staffMap: WithPrimaryKey<ITranslationMap<IStaffer>>[],
  practitioners: INamedDocument<IStaffer>[]
): INamedDocument<IStaffer> | undefined {
  const practitionerMap = getPractitionerOrDefaultMapping(
    practitionerId,
    staffMap
  )?.destinationIdentifier;

  if (!practitionerMap) {
    return;
  }

  const practitioner = practitioners.find((searchPractitioner) =>
    isSameRef(searchPractitioner.ref, practitionerMap)
  );

  return practitioner ? stafferToNamedDoc(practitioner) : undefined;
}
